// Passwordless sign-in. One email carries both ways in: a link to click, and a
// short code to type. The link is the happy path; the code is what saves the
// person reading mail on another device, or behind a client that mangles links.

import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { COMPANY } from "../lib/company";
import { authErrorMessage } from "../lib/authError";
import { BrandLockup } from "../components/BrandMark";
import { safeNext } from "../lib/redirect";

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 6.5h16v11H4z" />
      <path d="m4.5 7 7.5 6 7.5-6" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m21 3-7.4 18-3.3-7.3L3 10.4 21 3Z" />
      <path d="m10.3 13.7 4.2-4.2" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3 5.5 5.7v5.5c0 4.2 2.6 7.8 6.5 9.8 3.9-2 6.5-5.6 6.5-9.8V5.7L12 3Z" />
      <path d="m9.2 12 1.8 1.8 3.8-4" />
    </svg>
  );
}

export function SignIn() {
  const [params] = useSearchParams();
  // Where they were headed before the gate stopped them — an invitation link,
  // usually. Validated as a plain in-app path before it is put anywhere near a
  // redirect URL (see lib/redirect.ts).
  const next = safeNext(params.get("next"));
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function sendLink(): Promise<boolean> {
    setBusy(true);
    setError(null);
    setNotice(null);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      // /start decides where this person actually needs to be — the
      // connections screen on a first sign-in, the composer thereafter
      // (ADR-0016), or an invitation waiting for this address. `next` overrides
      // it for someone who arrived holding a link, so the invitation they
      // clicked is still the thing that happens after they sign in.
      // Both must be on the Supabase Auth redirect allowlist.
      options: { emailRedirectTo: `${window.location.origin}${next ?? "/start"}` },
    });
    setBusy(false);
    if (error) {
      setError(authErrorMessage(error));
      return false;
    }
    return true;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (await sendLink()) setSent(true);
  }

  async function resend() {
    if (await sendLink()) {
      setCode("");
      setNotice("A new email is on its way. The earlier code no longer works.");
    }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    // The code and the link are the same one-time token, so "email" is the type
    // for both. On success the session arrives via onAuthStateChange and the
    // route sends us to `next` or /start — nothing left to do here but report
    // a failure.
    const { error } = await supabase.auth.verifyOtp({
      email,
      token: code.trim(),
      type: "email",
    });
    setBusy(false);
    if (error) setError(authErrorMessage(error));
  }

  return (
    <div className="auth-page">
      <span className="auth-orbit auth-orbit-left" aria-hidden="true" />
      <span className="auth-orbit auth-orbit-right" aria-hidden="true" />
      <span className="auth-spark auth-spark-blue" aria-hidden="true">✦</span>
      <span className="auth-spark auth-spark-pink" aria-hidden="true">✦</span>

      <div className="auth-hero">
        <div className="auth-brand"><BrandLockup /></div>
        <h1>Sign in to {COMPANY.product}</h1>
        {/* Said before the form, not after it: someone who followed an
            invitation link needs to know it survived the detour. */}
        {next?.startsWith("/join") && (
          <p className="auth-lede" role="status">
            <strong>You were invited to a workspace.</strong> Sign in and we will take you
            straight to it.
          </p>
        )}
        <p className="auth-lede">
          We email you a one-time link and a one-time code.<br />
          There is no password to remember, and no password for anyone to steal.
        </p>

        {sent
          ? <div className="auth-card auth-sent-card">
              <div className="auth-card-heading">
                <span className="auth-success-icon"><MailIcon /></span>
                <div>
                  <h2>Check your inbox</h2>
                  <p>We sent a sign-in link to <strong>{email}</strong>.</p>
                </div>
              </div>
              <p className="auth-helper">
                It expires shortly. If it does not arrive within a minute, check your spam folder.
              </p>

              <form onSubmit={verify}>
                <label htmlFor="code">Or enter the code from that email</label>
                <div className="auth-input-wrap auth-code-input">
                  <input id="code" name="code" type="text" inputMode="numeric" required
                         autoComplete="one-time-code" autoFocus pattern="[0-9]*"
                         maxLength={8} value={code}
                         onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                         placeholder="123456" />
                </div>

                {error && <p className="err auth-message" role="alert">{error}</p>}
                {notice && <p className="muted auth-message" role="status">{notice}</p>}

                <button className="primary auth-submit" disabled={busy || code.trim().length < 6}>
                  <ShieldIcon />
                  {busy ? "Verifying…" : "Verify code"}
                </button>
              </form>

              <div className="auth-secondary-actions">
                <button type="button" className="link" disabled={busy} onClick={resend}>
                  Send it again
                </button>
                <span aria-hidden="true">·</span>
                <button type="button" className="link" disabled={busy}
                        onClick={() => { setSent(false); setCode(""); setError(null); setNotice(null); }}>
                  Use a different address
                </button>
              </div>
            </div>
          : <form className="auth-card" onSubmit={submit}>
              <label htmlFor="email">Email</label>
              <div className="auth-input-wrap">
                <span className="auth-field-icon"><MailIcon /></span>
                <input id="email" type="email" required autoComplete="email" value={email}
                       onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              </div>
              {error && <p className="err auth-message" role="alert">{error}</p>}
              <button className="primary auth-submit" disabled={busy}>
                <SendIcon />
                {busy ? "Sending…" : "Send sign-in link"}
              </button>
              <p className="auth-terms">
                <span className="auth-mini-shield"><ShieldIcon /></span>
                <span>
                  By continuing you agree to our <Link to="/terms">Terms of Service</Link> and{" "}
                  <Link to="/privacy">Privacy Policy</Link>.
                </span>
              </p>
            </form>}

        <div className="auth-security-note">
          <span className="auth-rule" />
          <span className="auth-note-icon"><ShieldIcon /></span>
          <span className="auth-rule" />
          <p>
            Connecting a social account later happens on Facebook&rsquo;s and Google&rsquo;s own sites —{" "}
            <Link to="/security">here is exactly what we can and cannot see</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
